import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { GameDifficulty } from "@/games/common/types";
import {
  GAMES_COMFORT_WIDTH,
  clampGamesWidth,
  snapWidthForMode,
  type GamesPanelSide,
  type GamesSizeMode,
} from "../constants";

export type GamesPortalView = "menu" | "playing";

export interface GamesSessionHud {
  score: number;
  level: number;
  highScore: number;
  status: "playing" | "paused" | "over";
}

interface OpenPortalOptions {
  view?: GamesPortalView;
  selectedGameId?: string | null;
}

interface GamesPortalState {
  isOpen: boolean;
  isCollapsed: boolean;
  isFullscreen: boolean;
  view: GamesPortalView;
  selectedGameId: string | null;
  difficulty: GameDifficulty;
  panelWidth: number;
  sizeMode: GamesSizeMode;
  panelSide: GamesPanelSide;
  sessionHud: GamesSessionHud | null;

  openPortal: (options?: OpenPortalOptions) => void;
  closePortal: () => void;
  toggleCollapsed: () => void;
  toggleFullscreen: () => void;
  setDifficulty: (difficulty: GameDifficulty) => void;
  startGame: (gameId: string) => void;
  backToMenu: () => void;
  resetSession: () => void;
  setPanelWidth: (width: number) => void;
  setSizeMode: (mode: Exclude<GamesSizeMode, "custom"> | "custom") => void;
  setPanelSide: (side: GamesPanelSide) => void;
  restorePanel: () => void;
  setSessionHud: (hud: GamesSessionHud | null) => void;
}

const initialSession = {
  isOpen: false,
  isCollapsed: false,
  isFullscreen: false,
  view: "menu" as GamesPortalView,
  selectedGameId: null as string | null,
  difficulty: "medium" as GameDifficulty,
  sessionHud: null as GamesSessionHud | null,
};

export const useGamesPortalStore = create<GamesPortalState>()(
  persist(
    (set) => ({
      ...initialSession,
      panelWidth: GAMES_COMFORT_WIDTH,
      sizeMode: "comfort",
      panelSide: "right",

      openPortal: (options) =>
        set({
          isOpen: true,
          isCollapsed: false,
          isFullscreen: false,
          view: options?.view ?? "menu",
          selectedGameId: options?.selectedGameId ?? null,
        }),

      closePortal: () =>
        set((state) => ({
          ...initialSession,
          panelWidth: state.panelWidth,
          sizeMode: state.sizeMode === "full" ? "comfort" : state.sizeMode,
          panelSide: state.panelSide,
        })),

      toggleCollapsed: () =>
        set((state) => ({
          isCollapsed: !state.isCollapsed,
          isFullscreen: state.isCollapsed ? state.isFullscreen : false,
        })),

      toggleFullscreen: () =>
        set((state) =>
          state.isFullscreen
            ? {
                isFullscreen: false,
                sizeMode:
                  state.sizeMode === "full" ? "comfort" : state.sizeMode,
                panelWidth:
                  state.sizeMode === "full"
                    ? snapWidthForMode("comfort")
                    : state.panelWidth,
              }
            : {
                isFullscreen: true,
                isCollapsed: false,
                sizeMode: "full",
              }
        ),

      setDifficulty: (difficulty) => set({ difficulty }),

      startGame: (gameId) =>
        set({
          isOpen: true,
          isCollapsed: false,
          view: "playing",
          selectedGameId: gameId,
        }),

      backToMenu: () =>
        set({
          view: "menu",
          selectedGameId: null,
          sessionHud: null,
        }),

      resetSession: () =>
        set({
          view: "menu",
          selectedGameId: null,
          difficulty: "medium",
          sessionHud: null,
        }),

      setPanelWidth: (width) =>
        set({
          panelWidth: clampGamesWidth(width),
        }),

      setSizeMode: (mode) =>
        set(() => {
          if (mode === "full") {
            return {
              sizeMode: "full",
              isFullscreen: true,
              isCollapsed: false,
            };
          }
          if (mode === "custom") {
            return {
              sizeMode: "custom",
              isFullscreen: false,
              isCollapsed: false,
            };
          }
          return {
            sizeMode: mode,
            isFullscreen: false,
            isCollapsed: false,
            panelWidth: snapWidthForMode(mode),
          };
        }),

      setPanelSide: (panelSide) => set({ panelSide }),

      restorePanel: () =>
        set({
          isOpen: true,
          isCollapsed: false,
          isFullscreen: false,
        }),

      setSessionHud: (sessionHud) => set({ sessionHud }),
    }),
    {
      name: "games-portal-storage",
      partialize: (state) => ({
        panelWidth: state.panelWidth,
        sizeMode: state.sizeMode === "full" ? "comfort" : state.sizeMode,
        panelSide: state.panelSide,
      }),
      merge: (persisted, current) => {
        const stored = persisted as
          | Partial<
              Pick<GamesPortalState, "panelWidth" | "sizeMode" | "panelSide">
            >
          | undefined;
        const sizeMode =
          stored?.sizeMode === "full" || !stored?.sizeMode
            ? "comfort"
            : stored.sizeMode;
        const width =
          sizeMode === "compact" || sizeMode === "comfort"
            ? snapWidthForMode(sizeMode)
            : clampGamesWidth(stored?.panelWidth ?? current.panelWidth);
        return {
          ...current,
          ...stored,
          sizeMode,
          panelWidth: width,
          panelSide: stored?.panelSide === "left" ? "left" : "right",
        };
      },
    }
  )
);
